import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { ShieldCheck, AlertTriangle, RefreshCw, Wrench, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

const IssueRow = ({ issue, onFix, fixing }) => (
  <div className="py-3 border-b last:border-0 border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`sku-issue-${issue.product_id}`}>
    <div className="min-w-0">
      <div className="font-semibold text-slate-900 truncate">{issue.name}</div>
      <div className="text-sm text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{issue.issue}</div>
      <div className="text-xs text-slate-500 mt-0.5">Retail {fmt(issue.price)} · Wholesale {fmt(issue.wholesale_price)} · CJ cost {fmt(issue.cj_cost_now)}</div>
    </div>
    {issue.fixable && (
      <Button size="sm" onClick={() => onFix(issue)} disabled={fixing} className="rounded-full bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid={`sku-fix-price-${issue.product_id}`}>
        {fixing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
        Fix price
      </Button>
    )}
  </div>
);

export const SkuHealthPanel = () => {
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/sku-health");
    setRun(data && data.run_id ? data : null);
    return data;
  }, []);

  useEffect(() => {
    load();
    return () => { clearInterval(pollRef.current); };
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    const { data } = await api.post("/admin/sku-health/run");
    toast.info("Checking all SKUs with CJ — this takes about 2 minutes");
    pollRef.current = setInterval(async () => {
      const latest = await load();
      if (latest && latest.run_id === data.run_id) {
        clearInterval(pollRef.current);
        setRunning(false);
        toast.success(`Check complete — ${latest.issues.length} issue(s)`);
      }
    }, 5000);
  };

  const fix = async (issue) => {
    setFixingId(issue.product_id);
    try {
      const { data } = await api.post(`/admin/products/${issue.product_id}/cj-sync`);
      toast.success(`${issue.name}: now ${fmt(data.price)} retail / ${fmt(data.wholesale_price)} wholesale`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Price fix failed");
    } finally {
      setFixingId(null);
    }
  };

  const issues = run?.issues || [];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="sku-health-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-bold text-lg text-slate-900">SKU Health</div>
          <div className="text-sm text-slate-500" data-testid="sku-health-last-run">
            {run ? `Last check ${new Date(run.at).toLocaleString()} · ${run.checked} products checked` : "No check has run yet"} · runs daily 07:00 UTC
          </div>
        </div>
        <Button onClick={runNow} disabled={running} variant="outline" className="rounded-full" data-testid="sku-health-run-now">
          {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          {running ? "Checking…" : "Run check now"}
        </Button>
      </div>
      {run && issues.length === 0 && (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-xl p-4 text-sm font-medium" data-testid="sku-health-all-ok">
          <ShieldCheck className="w-5 h-5" /> All {run.checked} products are linked, available and priced above CJ cost.
        </div>
      )}
      {issues.length > 0 && (
        <div data-testid="sku-health-issues">
          {issues.map((i) => <IssueRow key={i.product_id} issue={i} onFix={fix} fixing={fixingId === i.product_id} />)}
        </div>
      )}
    </div>
  );
};
